import type { AvailabilityScope, CalendarContract } from './types';

/**
 * Which contracts a scope shows (MCV-1821).
 *
 * A JOB contract is the provider's own commitment, tied to no service listing, so it shows in
 * every scope. A SERVICE contract belongs to the listing it was made through: it shows in "All
 * availability" and in that listing's own scope, and nowhere else (not in General, not in
 * another listing's).
 */
export const contractsForScope = (
  contracts: readonly CalendarContract[],
  scope: AvailabilityScope
): CalendarContract[] =>
  contracts.filter((contract) => {
    if (contract.listingType === 'JOB') return true;
    if (scope.kind === 'all') return true;
    return scope.kind === 'listing' && contract.providerServiceListingId === scope.listingId;
  });

/** The contracts starting on each date, in the order given. */
export const contractsByStartDate = (
  contracts: readonly CalendarContract[]
): Map<string, CalendarContract[]> => {
  const byDate = new Map<string, CalendarContract[]>();
  for (const contract of contracts) {
    byDate.set(contract.startDate, [...(byDate.get(contract.startDate) ?? []), contract]);
  }
  return byDate;
};

/**
 * [MCV-1844] How a contract status reads to the provider. Matches the contract pages
 * (PENDING is "Upcoming" there too); an unknown status falls back to a capitalised form.
 */
const CONTRACT_STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  PENDING: 'Upcoming',
  COMPLETED: 'Completed',
  TERMINATED: 'Terminated',
};

export const contractStatusLabel = (status: string): string =>
  CONTRACT_STATUS_LABEL[status] ??
  (status ? status.charAt(0) + status.slice(1).toLowerCase() : 'Unknown');

export const contractStartLabel = (contract: CalendarContract): string =>
  `Contract starts: ${contract.title} · ${contractStatusLabel(contract.status)}`;

/** Whether the contract's date span includes the date (an open-ended contract never ends). */
export const contractCoversDate = (contract: CalendarContract, isoDate: string): boolean =>
  contract.startDate <= isoDate && (contract.endDate == null || isoDate <= contract.endDate);

/** The contracts that cover at least one of the dates, in the order given. */
export const contractsCoveringDates = (
  contracts: readonly CalendarContract[],
  isoDates: readonly string[]
): CalendarContract[] =>
  contracts.filter((contract) => isoDates.some((date) => contractCoversDate(contract, date)));
